import { Command } from 'commander';
import { WorkflowEngine, WorkflowStatus } from '../core/workflow/workflow-engine';
import { WorkflowTemplateLibrary } from '../core/workflow/workflow-templates';
import { ConfigManager } from '../core/config-manager';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import { Table } from 'console-table-printer';
import inquirer from 'inquirer';
import ora from 'ora';

// 创建工作流命令
export function createWorkflowCommand(): Command {
  const workflow = new Command('workflow')
    .description('Manage workflows and templates');

  const configManager = new ConfigManager();
  const engine = new WorkflowEngine(configManager);
  const templateLibrary = new WorkflowTemplateLibrary();

  // 列出所有可用工作流
  workflow
    .command('list')
    .description('List all available workflows')
    .option('-s, --status <status>', 'Filter by status')
    .option('-t, --template', 'List templates instead of instances')
    .action(async (options) => {
      if (options.template) {
        // 列出模板
        const templates = templateLibrary.getAll();
        
        if (templates.length === 0) {
          console.log(chalk.yellow('No templates available'));
          return;
        }

        const table = new Table({
          columns: [
            { name: 'id', alignment: 'left', color: 'cyan' },
            { name: 'name', alignment: 'left' },
            { name: 'category', alignment: 'left', color: 'yellow' },
            { name: 'description', alignment: 'left' }
          ]
        });

        templates.forEach(template => {
          table.addRow({
            id: template.id,
            name: template.name,
            category: template.category,
            description: template.description
          });
        });

        table.printTable();
      } else {
        // 列出实例
        const instances = engine.getInstances({ status: options.status });
        
        if (instances.length === 0) {
          console.log(chalk.yellow('No workflow instances found'));
          return;
        }

        const table = new Table({
          columns: [
            { name: 'id', alignment: 'left', color: 'cyan' },
            { name: 'name', alignment: 'left' },
            { name: 'status', alignment: 'center' },
            { name: 'started', alignment: 'left' },
            { name: 'current_step', alignment: 'left' }
          ]
        });

        instances.forEach(instance => {
          const definition = engine['definitions'].get(instance.definitionId);
          const statusColor = getStatusColor(instance.status);
          
          table.addRow({
            id: instance.id.substring(0, 8),
            name: definition?.name || 'Unknown',
            status: chalk[statusColor](instance.status),
            started: new Date(instance.startedAt).toLocaleString(),
            current_step: instance.currentStep || 'N/A'
          });
        });

        table.printTable();
      }
    });

  // 启动工作流
  workflow
    .command('start <type>')
    .description('Start a new workflow')
    .option('-p, --params <json>', 'Parameters as JSON')
    .option('-i, --interactive', 'Interactive parameter input')
    .option('-d, --dry-run', 'Simulate workflow execution')
    .action(async (type, options) => {
      const spinner = ora('Preparing workflow...').start();
      
      try {
        // 查找模板
        const template = templateLibrary.get(type);
        if (!template) {
          spinner.fail(`Template not found: ${type}`);
          console.log(chalk.yellow('\nAvailable templates:'));
          const templates = templateLibrary.getAll();
          templates.forEach(t => {
            console.log(chalk.gray(`  - ${t.id}: ${t.name}`));
          });
          return;
        }

        spinner.text = 'Gathering parameters...';

        // 收集参数
        let parameters = {};
        if (options.params) {
          parameters = JSON.parse(options.params);
        } else if (options.interactive && template.parameters) {
          spinner.stop();
          const answers = await inquirer.prompt(
            template.parameters.map(param => ({
              type: param.type === 'boolean' ? 'confirm' : 
                    param.enum ? 'list' : 'input',
              name: param.name,
              message: param.description || param.name,
              default: param.default,
              choices: param.enum,
              validate: param.required ? 
                (input) => input ? true : `${param.name} is required` : undefined
            }))
          );
          parameters = answers;
          spinner.start();
        }

        spinner.text = 'Creating workflow instance...';

        // 创建并注册工作流定义
        const definition = templateLibrary.createFromTemplate(type, parameters);
        engine.registerDefinition(definition);

        // 启动工作流
        const instance = await engine.start(definition.id, {
          variables: parameters,
          dryRun: options.dryRun
        });

        spinner.succeed(`Workflow started: ${instance.id}`);
        
        console.log(chalk.green('\n✓ Workflow Details:'));
        console.log(chalk.gray(`  ID: ${instance.id}`));
        console.log(chalk.gray(`  Name: ${definition.name}`));
        console.log(chalk.gray(`  Status: ${instance.status}`));
        console.log(chalk.gray(`  Steps: ${definition.steps.length}`));
        
        if (!options.dryRun) {
          console.log(chalk.yellow('\nUse "workflow status" to monitor progress'));
        }

      } catch (error) {
        spinner.fail(`Failed to start workflow: ${error.message}`);
        logger.error('Workflow start error:', error);
      }
    });

  // 查看工作流状态
  workflow
    .command('status [id]')
    .description('View workflow status')
    .option('-d, --detailed', 'Show detailed information')
    .option('-f, --follow', 'Follow workflow progress')
    .action(async (id, options) => {
      if (!id) {
        // 显示所有活跃工作流
        const instances = engine.getInstances({
          status: WorkflowStatus.RUNNING
        });
        
        if (instances.length === 0) {
          console.log(chalk.yellow('No active workflows'));
          return;
        }

        console.log(chalk.cyan('Active Workflows:\n'));
        instances.forEach(instance => {
          const definition = engine['definitions'].get(instance.definitionId);
          console.log(chalk.white(`${instance.id.substring(0, 8)} - ${definition?.name || 'Unknown'}`));
        });
        
        return;
      }

      // 显示特定工作流状态
      const showStatus = async () => {
        const instance = engine.getInstance(id);
        if (!instance) {
          console.log(chalk.red(`Workflow not found: ${id}`));
          return false;
        }

        const definition = engine['definitions'].get(instance.definitionId);
        if (!definition) {
          console.log(chalk.red('Workflow definition not found'));
          return false;
        }

        console.clear();
        console.log(chalk.cyan('Workflow Status\n'));
        console.log(chalk.white(`ID: ${instance.id}`));
        console.log(chalk.white(`Name: ${definition.name}`));
        console.log(chalk.white(`Status: ${getStatusBadge(instance.status)}`));
        console.log(chalk.white(`Started: ${new Date(instance.startedAt).toLocaleString()}`));
        
        if (instance.completedAt) {
          console.log(chalk.white(`Completed: ${new Date(instance.completedAt).toLocaleString()}`));
        }

        console.log(chalk.cyan('\nSteps:\n'));
        
        const table = new Table({
          columns: [
            { name: 'step', alignment: 'left' },
            { name: 'status', alignment: 'center' },
            { name: 'type', alignment: 'left', color: 'gray' },
            { name: 'dependencies', alignment: 'left', color: 'gray' }
          ]
        });

        definition.steps.forEach(step => {
          const isCurrent = instance.currentStep === step.id;
          const statusBadge = getStatusBadge(step.status);
          
          table.addRow({
            step: isCurrent ? chalk.yellow(`▶ ${step.name}`) : step.name,
            status: statusBadge,
            type: step.type,
            dependencies: step.dependencies.join(', ') || 'none'
          });
        });

        table.printTable();

        if (options.detailed) {
          console.log(chalk.cyan('\nHistory:\n'));
          const history = engine.getHistory(instance.id);
          history.slice(-10).forEach(event => {
            const time = new Date(event.timestamp).toLocaleTimeString();
            const icon = event.type === 'step_completed' ? '✓' : 
                        event.type === 'step_failed' ? '✗' : '•';
            console.log(chalk.gray(`${time} ${icon} ${event.type} ${event.stepId || ''}`));
          });
        }

        return instance.status === WorkflowStatus.RUNNING || 
               instance.status === WorkflowStatus.PAUSED;
      };

      if (options.follow) {
        // 持续监控
        let running = true;
        while (running) {
          running = await showStatus();
          if (running) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      } else {
        await showStatus();
      }
    });

  // 暂停工作流
  workflow
    .command('pause <id>')
    .description('Pause a running workflow')
    .action(async (id) => {
      const spinner = ora('Pausing workflow...').start();
      
      try {
        await engine.pause(id);
        spinner.succeed('Workflow paused successfully');
      } catch (error) {
        spinner.fail(`Failed to pause workflow: ${error.message}`);
      }
    });

  // 恢复工作流
  workflow
    .command('resume <id>')
    .description('Resume a paused workflow')
    .action(async (id) => {
      const spinner = ora('Resuming workflow...').start();
      
      try {
        await engine.resume(id);
        spinner.succeed('Workflow resumed successfully');
      } catch (error) {
        spinner.fail(`Failed to resume workflow: ${error.message}`);
      }
    });

  // 取消工作流
  workflow
    .command('cancel <id>')
    .description('Cancel a workflow')
    .option('-r, --reason <reason>', 'Cancellation reason')
    .action(async (id, options) => {
      const spinner = ora('Cancelling workflow...').start();
      
      try {
        await engine.cancel(id, options.reason);
        spinner.succeed('Workflow cancelled successfully');
      } catch (error) {
        spinner.fail(`Failed to cancel workflow: ${error.message}`);
      }
    });

  // 查看工作流历史
  workflow
    .command('history [id]')
    .description('View workflow history')
    .option('-l, --limit <n>', 'Limit number of results', '10')
    .action(async (id, options) => {
      if (id) {
        // 特定工作流的历史
        const history = engine.getHistory(id);
        
        if (history.length === 0) {
          console.log(chalk.yellow('No history found'));
          return;
        }

        console.log(chalk.cyan(`Workflow History: ${id}\n`));
        
        history.slice(-parseInt(options.limit)).forEach(event => {
          const time = new Date(event.timestamp).toLocaleString();
          const icon = getEventIcon(event.type);
          const color = getEventColor(event.type);
          
          console.log(chalk[color](`${time} ${icon} ${event.type}`));
          if (event.stepId) {
            console.log(chalk.gray(`  Step: ${event.stepId}`));
          }
          if (event.error) {
            console.log(chalk.red(`  Error: ${event.error}`));
          }
        });
      } else {
        // 所有工作流的历史
        const instances = engine.getInstances({ limit: parseInt(options.limit) });
        
        if (instances.length === 0) {
          console.log(chalk.yellow('No workflow history'));
          return;
        }

        console.log(chalk.cyan('Recent Workflows:\n'));
        
        const table = new Table({
          columns: [
            { name: 'id', alignment: 'left', color: 'cyan' },
            { name: 'name', alignment: 'left' },
            { name: 'status', alignment: 'center' },
            { name: 'started', alignment: 'left' },
            { name: 'duration', alignment: 'right' }
          ]
        });

        instances.forEach(instance => {
          const definition = engine['definitions'].get(instance.definitionId);
          const duration = instance.completedAt ? 
            formatDuration(instance.completedAt - instance.startedAt) : 
            'Running';
          
          table.addRow({
            id: instance.id.substring(0, 8),
            name: definition?.name || 'Unknown',
            status: getStatusBadge(instance.status),
            started: new Date(instance.startedAt).toLocaleString(),
            duration
          });
        });

        table.printTable();
      }
    });

  // 模板管理命令
  const template = workflow
    .command('template')
    .description('Manage workflow templates');

  // 列出模板
  template
    .command('list')
    .description('List available templates')
    .option('-c, --category <category>', 'Filter by category')
    .action((options) => {
      const templates = templateLibrary.getAll(options.category);
      
      if (templates.length === 0) {
        console.log(chalk.yellow('No templates found'));
        return;
      }

      // 按类别分组
      const grouped = templates.reduce((acc, template) => {
        if (!acc[template.category]) {
          acc[template.category] = [];
        }
        acc[template.category].push(template);
        return acc;
      }, {} as Record<string, typeof templates>);

      Object.entries(grouped).forEach(([category, templates]) => {
        console.log(chalk.cyan(`\n${category.toUpperCase()}:`));
        
        templates.forEach(template => {
          console.log(chalk.white(`  ${template.id}`));
          console.log(chalk.gray(`    ${template.description}`));
          
          if (template.parameters && template.parameters.length > 0) {
            console.log(chalk.gray(`    Parameters: ${template.parameters.map(p => p.name).join(', ')}`));
          }
        });
      });
    });

  // 显示模板详情
  template
    .command('show <id>')
    .description('Show template details')
    .action((id) => {
      const template = templateLibrary.get(id);
      
      if (!template) {
        console.log(chalk.red(`Template not found: ${id}`));
        return;
      }

      console.log(chalk.cyan('Template Details\n'));
      console.log(chalk.white(`ID: ${template.id}`));
      console.log(chalk.white(`Name: ${template.name}`));
      console.log(chalk.white(`Category: ${template.category}`));
      console.log(chalk.white(`Description: ${template.description}`));
      
      if (template.parameters && template.parameters.length > 0) {
        console.log(chalk.cyan('\nParameters:\n'));
        
        template.parameters.forEach(param => {
          const required = param.required ? chalk.red('*') : '';
          console.log(chalk.white(`  ${param.name}${required}`));
          console.log(chalk.gray(`    Type: ${param.type}`));
          if (param.description) {
            console.log(chalk.gray(`    Description: ${param.description}`));
          }
          if (param.default !== undefined) {
            console.log(chalk.gray(`    Default: ${param.default}`));
          }
          if (param.enum) {
            console.log(chalk.gray(`    Options: ${param.enum.join(', ')}`));
          }
        });
      }

      console.log(chalk.cyan('\nWorkflow Steps:\n'));
      
      const table = new Table({
        columns: [
          { name: 'id', alignment: 'left', color: 'yellow' },
          { name: 'name', alignment: 'left' },
          { name: 'type', alignment: 'left', color: 'gray' },
          { name: 'dependencies', alignment: 'left', color: 'gray' }
        ]
      });

      template.definition.steps.forEach(step => {
        table.addRow({
          id: step.id,
          name: step.name,
          type: step.type,
          dependencies: step.dependencies.join(', ') || 'none'
        });
      });

      table.printTable();
    });

  // 创建自定义模板
  template
    .command('create')
    .description('Create a custom workflow template')
    .option('-f, --file <path>', 'Template definition file (JSON/YAML)')
    .option('-i, --interactive', 'Interactive template creation')
    .action(async (options) => {
      if (options.interactive) {
        console.log(chalk.cyan('Interactive template creation coming soon...'));
        // TODO: 实现交互式模板创建
      } else if (options.file) {
        console.log(chalk.cyan('File-based template creation coming soon...'));
        // TODO: 实现基于文件的模板创建
      } else {
        console.log(chalk.yellow('Please specify --file or --interactive'));
      }
    });

  return workflow;
}

// 辅助函数
function getStatusColor(status: WorkflowStatus): string {
  switch (status) {
    case WorkflowStatus.COMPLETED:
      return 'green';
    case WorkflowStatus.RUNNING:
      return 'blue';
    case WorkflowStatus.FAILED:
      return 'red';
    case WorkflowStatus.PAUSED:
      return 'yellow';
    case WorkflowStatus.CANCELLED:
      return 'gray';
    default:
      return 'white';
  }
}

function getStatusBadge(status: string): string {
  switch (status) {
    case 'completed':
      return chalk.green('✓ Completed');
    case 'running':
      return chalk.blue('● Running');
    case 'failed':
      return chalk.red('✗ Failed');
    case 'paused':
      return chalk.yellow('⏸ Paused');
    case 'cancelled':
      return chalk.gray('⊘ Cancelled');
    case 'pending':
      return chalk.gray('○ Pending');
    case 'skipped':
      return chalk.gray('⊝ Skipped');
    default:
      return status;
  }
}

function getEventIcon(type: string): string {
  switch (type) {
    case 'started':
      return '▶';
    case 'step_completed':
      return '✓';
    case 'step_failed':
      return '✗';
    case 'paused':
      return '⏸';
    case 'resumed':
      return '▶';
    case 'completed':
      return '✅';
    case 'failed':
      return '❌';
    default:
      return '•';
  }
}

function getEventColor(type: string): string {
  if (type.includes('completed')) return 'green';
  if (type.includes('failed')) return 'red';
  if (type.includes('paused')) return 'yellow';
  return 'gray';
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}